/**
 * Reading the finished grid for somewhere to put things.
 *
 * Everything the placement policy needs to know about a cell is worked out
 * once, here, into flat typed arrays: which sides of it are solid, whether it
 * is a threshold, whether it is a dead end, how far it is from the spawn, and
 * whether it lies on the way to an exit. `props.ts` then does nothing but score
 * and draw.
 *
 * Four of the fields are worth spelling out.
 *
 * **Hosts.** A wall-hugging prop may only be placed on a cell that has a solid
 * orthogonal neighbour. That test predates collision: it was the only defence
 * against a player walking through a filing cabinet.
 *
 * **Frees.** A cell in the clear middle of a room, with all four orthogonal
 * neighbours walkable and no threshold within a step. This is the pool the
 * `'floor-free'` mount draws from, and it only exists because those props are
 * solid — the player is stopped by a bathtub standing in the open rather than
 * walking through it, so it no longer has to hug a wall to be honest.
 *
 * **Blocked.** Which cells the solid props placed so far have claimed. The
 * placer reads it to make sure a route survives; nothing outside generation
 * uses it, because at runtime the real answer is `GeneratedLevel.propIndex`.
 *
 * **Route slack.** The exits arrive on `PropContext` exactly as `placement.ts`
 * placed them, so `routeSlack` — the number of extra cells you would walk by
 * routing through a given cell on your way to the nearest exit — is exact
 * rather than estimated. That is what lets an arrow be scratched somewhere
 * worth following instead of somewhere arbitrary, and it is why signs can be
 * reserved for the cells that can actually see a way out.
 */

import { bfsDistances, UNREACHABLE } from '../../core/grid';
import { hash2, smoothstep } from '../../core/math';
import {
  Cell,
  type ExitPoint,
  type Grid,
  type ItemSpot,
  type LevelSpec,
  type Region,
  type RegionKind,
  type RNG,
} from '../../core/types';
import { buildRegionIndex } from './site';

/** Direction order used by every mask in this module: +X, -X, +Y, -Y. */
export const DIR_DX = [1, -1, 0, 0] as const;
export const DIR_DY = [0, 0, 1, -1] as const;
/** Index of the opposite direction. */
export const DIR_OPPOSITE = [1, 0, 3, 2] as const;

/**
 * Metres of guaranteed nothing around the spawn, measured along the floor
 * rather than as the crow flies. Thirty metres of shortest path through a maze
 * is well over thirty seconds of actually finding your way through it, and the
 * ramp above it is slow enough that the first real find is usually much further
 * out than the floor.
 */
const QUIET_METRES = 30;
/** Metres over which scenery fades back in. */
const RAMP_METRES = 90;
/** Landmarks use a shorter exclusion: a tally mark two rooms out still lands. */
const LANDMARK_QUIET_METRES = 12;
const LANDMARK_RAMP_METRES = 46;

/**
 * How much scenery a region kind earns. Corridors are kept clearer than rooms
 * on purpose: you walk down a hall, you stop in a room, and a closet is where
 * junk actually accumulates.
 */
const REGION_WEIGHT: Record<RegionKind, number> = {
  room: 1.0,
  // Corridors get less than rooms but not much less. A long hall is the only
  // thing on Level 0 a player can navigate by, and "the corridor with the
  // payphone in it" is worth more than any four rooms.
  hall: 0.85,
  pillar: 0.8,
  closet: 1.8,
  flooded: 0.15,
  atrium: 0.75,
  warren: 1.15,
};

/** Everything `props.ts` needs to know about the level, precomputed. */
export interface PropSites {
  readonly spec: LevelSpec;
  readonly grid: Grid;
  readonly regions: readonly Region[];
  readonly dist: Uint16Array;
  readonly maxDist: number;
  readonly walkableCount: number;

  /** Bit `d` set when the neighbour in direction `d` is solid. */
  readonly solidMask: Uint8Array;
  /** Walkable orthogonal neighbours, 0..4. */
  readonly openCount: Uint8Array;
  /** 1 when the cell is a doorway or orthogonally touches one. */
  readonly nearDoor: Uint8Array;
  /** 1 when the cell or a neighbour within one step is a three-way-or-better fork. */
  readonly nearJunction: Uint8Array;
  /** Region id per cell, later regions winning. -1 when unlabelled. */
  readonly regionAt: Int32Array;

  /** Flat cell indices that may host a wall-hugging prop, row-major. */
  readonly hosts: Int32Array;
  /** Position in `hosts` for a cell, -1 when the cell is not a host. */
  readonly hostAt: Int32Array;
  /** Cluster-seed suitability per host. Damped as props are placed. */
  readonly weight: Float32Array;
  /** Scratch buffer the size of `hosts`, reused by every scored draw. */
  readonly scratch: Float32Array;

  /** Flat cell indices a free-standing prop may occupy, row-major. */
  readonly frees: Int32Array;
  /** Position in `frees` for a cell, -1 when the cell is not one. */
  readonly freeAt: Int32Array;
  /** Scratch buffer the size of `frees`. */
  readonly freeScratch: Float32Array;

  /** Walkable cells in the 3x3 neighbourhood, 0..8. 8 is the middle of a room. */
  readonly openness: Uint8Array;

  /** 1 when a prop already occupies the cell. */
  readonly taken: Uint8Array;
  /**
   * 1 when a solid prop's footprint claims the cell, so the placer must treat
   * it as impassable when checking that a route survives. Written by `props.ts`
   * as it commits each solid prop.
   */
  readonly blocked: Uint8Array;
  /**
   * 1 when the player has to be able to stand here: the spawn, an exit, a
   * supply spot, and a ring of cells around each. Nothing solid may go here.
   */
  readonly reserved: Uint8Array;

  /** The cell each reachable exit stands in. Empty when the level has none. */
  readonly exitCells: readonly number[];
  /** BFS distance to the nearest exit. `UNREACHABLE` when there are none. */
  readonly exitDist: Uint16Array;
  /** Extra cells walked by routing through this cell to the nearest exit. */
  readonly routeSlack: Uint16Array;

  /** 0..1 ramp that keeps the area around the spawn empty. */
  quiet(cellIdx: number): number;
  /** The same ramp, relaxed, for landmarks. */
  landmarkQuiet(cellIdx: number): number;
}

/** Read the finished grid and precompute every placement input. */
export function analyzeSites(
  spec: LevelSpec,
  grid: Grid,
  regions: Region[],
  rng: RNG,
  dist: Uint16Array,
  exits: readonly ExitPoint[],
  items: readonly ItemSpot[] = [],
): PropSites {
  const { w, h, cells } = grid;
  const n = w * h;

  const solidMask = new Uint8Array(n);
  const openCount = new Uint8Array(n);
  const openness = new Uint8Array(n);
  const nearDoor = new Uint8Array(n);
  const nearJunction = new Uint8Array(n);
  const taken = new Uint8Array(n);
  const blocked = new Uint8Array(n);
  const reserved = new Uint8Array(n);

  let walkableCount = 0;
  let maxDist = 1;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const c = cells[i] as Cell;
      if (c === Cell.Void || c === Cell.Wall) continue;
      walkableCount++;
      const d = dist[i];
      if (d !== UNREACHABLE && d > maxDist) maxDist = d;

      let mask = 0;
      let open = 0;
      for (let k = 0; k < 4; k++) {
        const nx = x + DIR_DX[k];
        const ny = y + DIR_DY[k];
        if (grid.isSolid(nx, ny)) mask |= 1 << k;
        else open++;
      }
      solidMask[i] = mask;
      openCount[i] = open;

      let around = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          if (grid.isWalkable(x + dx, y + dy)) around++;
        }
      }
      openness[i] = around;
    }
  }

  // Everything the player must be able to reach, plus a ring around it. The
  // spawn is wherever the BFS started, which is the only cell at distance zero.
  for (let i = 0; i < n; i++) if (dist[i] === 0) reserve(grid, reserved, i % w, (i / w) | 0, 2);
  for (const exit of exits) {
    const c = grid.worldToCell(exit.pos.x, exit.pos.z);
    reserve(grid, reserved, c.cx, c.cy, 2);
  }
  for (const item of items) {
    const c = grid.worldToCell(item.pos.x, item.pos.z);
    reserve(grid, reserved, c.cx, c.cy, 1);
  }
  // Dead ends and small closets, whether or not the supply spots were handed
  // over. `supplies.ts` reserves nearly half of every level's pickups for
  // exactly these cells, and a filing tower standing in a one-cell dead end is
  // a granola bar the player can see and cannot reach. It costs almost nothing
  // to keep solid props out: `reserved` is only consulted for those, so a
  // tally mark or a camp still goes wherever it likes.
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (cells[i] === Cell.Void || cells[i] === Cell.Wall) continue;
      if (openCount[i] <= 1) reserved[i] = 1;
    }
  }
  for (const region of regions) {
    if (region.kind !== 'closet' || region.w * region.h > 12) continue;
    for (let y = region.y; y < region.y + region.h; y++) {
      for (let x = region.x; x < region.x + region.w; x++) {
        if (grid.inBounds(x, y)) reserved[y * w + x] = 1;
      }
    }
  }

  // Thresholds and forks. Both are one-step neighbourhoods, so they are cheap
  // and they capture the cell *next to* a four-way crossing — which is where a
  // sign can actually be painted, the crossing itself having no wall at all.
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (cells[i] === Cell.Void || cells[i] === Cell.Wall) continue;
      if (cells[i] === Cell.Doorway) nearDoor[i] = 1;
      if (openCount[i] >= 3) nearJunction[i] = 1;
      for (let k = 0; k < 4; k++) {
        const j = (y + DIR_DY[k]) * w + (x + DIR_DX[k]);
        if (cells[j] === Cell.Doorway) nearDoor[i] = 1;
        if (openCount[j] >= 3 && grid.isWalkable(x + DIR_DX[k], y + DIR_DY[k])) nearJunction[i] = 1;
      }
    }
  }

  const regionAt = buildRegionIndex({ spec, grid, rng, regions });

  // One seed for the whole clustering field, drawn from the level's stream so
  // the same seed produces the same lived-in pockets.
  const fieldSeed = rng.int(0, 0x7fffffff);
  const cellSize = grid.cellSize;

  const hostList: number[] = [];
  const hostAt = new Int32Array(n).fill(-1);
  const weightList: number[] = [];

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const c = cells[i] as Cell;
      // Doorways are the one place a prop must never be, pillars already have a
      // column through the middle of them, and water is dressed separately.
      if (c !== Cell.Floor && c !== Cell.Damp && c !== Cell.Open) continue;
      if (solidMask[i] === 0) continue;
      const d = dist[i];
      if (d === UNREACHABLE) continue;

      const q = ramp(d * cellSize, QUIET_METRES, RAMP_METRES);
      if (q <= 0) continue;

      const rid = regionAt[i];
      const kind: RegionKind = rid >= 0 ? regions[rid].kind : 'room';
      let wgt = REGION_WEIGHT[kind];
      wgt *= q;
      // Deeper reads as more lived-in: the far end of a level should feel like
      // more people gave up there.
      wgt *= 0.8 + 0.45 * (d / maxDist);
      wgt *= pocket(x * cellSize, y * cellSize, fieldSeed);
      if (isCorner(solidMask[i])) wgt *= 1.7;
      if (openCount[i] <= 1) wgt *= 2.4;
      else if (openCount[i] === 2) wgt *= 1.15;
      if (nearDoor[i]) wgt *= 0.55;

      if (wgt <= 0) continue;
      hostAt[i] = hostList.length;
      hostList.push(i);
      weightList.push(wgt);
    }
  }

  const hosts = Int32Array.from(hostList);
  const weight = Float32Array.from(weightList);

  // Cells a free-standing prop may stand in. The gate is deliberately blunt:
  // all four neighbours walkable and no threshold within a step, so anything
  // placed here is in the clear middle of a space rather than in the mouth of
  // one. Whether it actually leaves a route past is settled later, per prop,
  // against its real footprint.
  const freeList: number[] = [];
  const freeAt = new Int32Array(n).fill(-1);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const c = cells[i] as Cell;
      if (c !== Cell.Floor && c !== Cell.Damp && c !== Cell.Open && c !== Cell.Water) continue;
      if (openCount[i] !== 4) continue;
      if (nearDoor[i] || reserved[i]) continue;
      const d = dist[i];
      if (d === UNREACHABLE) continue;
      if (ramp(d * cellSize, QUIET_METRES, RAMP_METRES) <= 0) continue;
      freeAt[i] = freeList.length;
      freeList.push(i);
    }
  }
  const frees = Int32Array.from(freeList);

  const exitCells = exitAnchors(grid, dist, exits);
  const { exitDist, routeSlack } = routeField(grid, dist, exitCells);

  return {
    spec,
    grid,
    regions,
    dist,
    maxDist,
    walkableCount,
    solidMask,
    openCount,
    openness,
    nearDoor,
    nearJunction,
    regionAt,
    hosts,
    hostAt,
    weight,
    scratch: new Float32Array(hosts.length),
    frees,
    freeAt,
    freeScratch: new Float32Array(frees.length),
    taken,
    blocked,
    reserved,
    exitCells,
    exitDist,
    routeSlack,
    quiet(cellIdx: number): number {
      const d = dist[cellIdx];
      return d === UNREACHABLE ? 0 : ramp(d * cellSize, QUIET_METRES, RAMP_METRES);
    },
    landmarkQuiet(cellIdx: number): number {
      const d = dist[cellIdx];
      return d === UNREACHABLE ? 0 : ramp(d * cellSize, LANDMARK_QUIET_METRES, LANDMARK_RAMP_METRES);
    },
  };
}

/** Mark a Chebyshev disc of cells as somewhere the player must still be able to stand. */
function reserve(grid: Grid, reserved: Uint8Array, cx: number, cy: number, radius: number): void {
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const x = cx + dx;
      const y = cy + dy;
      if (!grid.inBounds(x, y)) continue;
      reserved[y * grid.w + x] = 1;
    }
  }
}

/** True when two perpendicular sides of the cell are solid. */
export function isCorner(mask: number): boolean {
  const horizontal = (mask & 0b0011) !== 0;
  const vertical = (mask & 0b1100) !== 0;
  return horizontal && vertical;
}

/**
 * True when the cell is a length of one-cell corridor: two ways out, directly
 * opposite each other. Bulky props are kept out of these — the player has to
 * walk through the middle, and a shelving unit there reads as a wall that is
 * not one.
 */
export function isThroughCorridor(mask: number): boolean {
  return mask === 0b0011 || mask === 0b1100;
}

/** Smooth 0..1 ramp, 0 below `from` metres and 1 above `to`. */
function ramp(metres: number, from: number, to: number): number {
  return smoothstep(from, to, metres);
}

/**
 * The clustering field. Two octaves of value noise, shaped hard so most of the
 * map sits near zero: long stretches of nothing are what make the pockets that
 * do have something register as places rather than as texture.
 *
 * Argued in metres rather than cells so a pocket is the same size on Level 2's
 * 2.6 m grid as on the Poolrooms' 3.4 m one — the coarse octave is about the
 * width of three or four rooms, the fine one about one.
 */
function pocket(mx: number, mz: number, seed: number): number {
  const n =
    0.62 * valueNoise(mx / 68, mz / 68, seed) + 0.38 * valueNoise(mx / 25, mz / 25, seed ^ 0x51ed);
  return 0.12 + 1.55 * smoothstep(0.34, 0.78, n);
}

function valueNoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy, seed);
  const b = hash2(ix + 1, iy, seed);
  const c = hash2(ix, iy + 1, seed);
  const d = hash2(ix + 1, iy + 1, seed);
  const top = a + (b - a) * ux;
  const bottom = c + (d - c) * ux;
  return top + (bottom - top) * uy;
}

/**
 * The cell each exit stands in, deduplicated and filtered to the ones the player
 * can actually reach. An exit whose own cell came out unreachable would poison
 * `routeSlack` for the whole level, so it is dropped rather than trusted.
 */
function exitAnchors(grid: Grid, dist: Uint16Array, exits: readonly ExitPoint[]): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  for (const exit of exits) {
    const { cx, cy } = grid.worldToCell(exit.pos.x, exit.pos.z);
    if (!grid.inBounds(cx, cy)) continue;
    const i = cy * grid.w + cx;
    if (seen.has(i)) continue;
    if (dist[i] === UNREACHABLE) continue;
    seen.add(i);
    out.push(i);
  }
  return out;
}

/**
 * Distance to the nearest estimated exit, and the detour cost of passing
 * through each cell on the way there. A cell with zero slack is on a shortest
 * route out; a cell with a slack of forty is a wrong turn.
 */
function routeField(
  grid: Grid,
  dist: Uint16Array,
  exitCells: readonly number[],
): { exitDist: Uint16Array; routeSlack: Uint16Array } {
  const n = grid.cells.length;
  const exitDist = new Uint16Array(n).fill(UNREACHABLE);
  const routeSlack = new Uint16Array(n).fill(UNREACHABLE);
  if (exitCells.length === 0) return { exitDist, routeSlack };

  const w = grid.w;
  for (const cell of exitCells) {
    const de = bfsDistances(grid, [{ cx: cell % w, cy: (cell / w) | 0 }]);
    const best = dist[cell];
    if (best === UNREACHABLE) continue;
    for (let i = 0; i < n; i++) {
      const a = de[i];
      if (a < exitDist[i]) exitDist[i] = a;
      const b = dist[i];
      if (a === UNREACHABLE || b === UNREACHABLE) continue;
      const slack = a + b - best;
      if (slack >= 0 && slack < routeSlack[i]) routeSlack[i] = slack;
    }
  }
  return { exitDist, routeSlack };
}
